// Format audit-event payloads into a CSV string. Ported from the Python
// redact-blur-engine's extract_audit_log.py / worker.py so client-side image
// redaction can produce the same audit log shape the server engine emits.

import { isNil } from 'lodash';

export interface AuditEventPayload {
  timestamp?: string;
  userName?: string;
  action?: string;
  md5sum?: string | null;
  details?: string;
}

export interface AuditEventRecord {
  payload?: AuditEventPayload;
}

const HEADERS = ['DATE', 'USER', 'ACTION', 'DETAILS'] as const;

/**
 * Leading characters a spreadsheet reads as the start of a FORMULA rather than
 * as text. A cell beginning with one of these is executed on open by Excel,
 * Sheets and LibreOffice alike.
 *
 * This matters here because audit DETAILS carries operator-controlled strings —
 * a Bates prefix, a redacted phrase, a filename — and this CSV is written as an
 * asset that ships beside a released production and gets opened by records
 * staff. A prefix of `=cmd|' /c calc'!A0` fits inside the 20-character affix
 * bound, is fully WinAnsi-encodable so the editor accepts it, and contains no
 * comma or quote, so RFC-4180 quoting alone leaves it bare.
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

// RFC 4180 CSV escaping: wrap a field in double-quotes if it contains a
// comma, quote, CR, or LF, and double up any embedded quotes. Plus the formula
// guard above — a leading apostrophe, which every spreadsheet reads as "the
// rest of this cell is text" and strips from the displayed value.
function escapeCsvField(value: string | undefined | null): string {
  if (isNil(value)) {
    return '';
  }
  const guarded = FORMULA_LEAD.test(value) ? `'${value}` : value;
  const needsQuoting = /[",\r\n]/.test(guarded);
  const escaped = guarded.replace(/"/g, '""');
  return needsQuoting ? `"${escaped}"` : escaped;
}

interface FormattedRow {
  DATE: string;
  USER: string;
  ACTION: string;
  DETAILS: string;
}

function formatRow(payload: AuditEventPayload): FormattedRow {
  const detailParts: string[] = [];

  if (payload.md5sum) {
    detailParts.push(`file hash: ${payload.md5sum}`);
  }
  if (payload.details) {
    detailParts.push(payload.details);
  }

  let action = payload.action ?? '';

  // Hack ported from the Python engine: when actions are long and contain a
  // JSON-ish blob, split the blob off into the DETAILS column so the ACTION
  // column stays readable in spreadsheets.
  if (action.length > 15) {
    const splitIndex = action.indexOf('{');
    if (splitIndex > 1) {
      detailParts.push(action.slice(splitIndex));
      action = action.slice(0, splitIndex);
    }
  }

  return {
    DATE: payload.timestamp ?? '',
    USER: payload.userName ?? '',
    ACTION: action,
    DETAILS: detailParts.join(' '),
  };
}

export function formatAuditEventsCsv(records: AuditEventRecord[]): string {
  const rows = records
    .map((r) => formatRow(r.payload ?? {}))
    // Most recent first — matches the Python engine's output.
    .sort((a, b) => (a.DATE < b.DATE ? 1 : a.DATE > b.DATE ? -1 : 0));

  const lines = [HEADERS.join(',')];
  for (const row of rows) {
    lines.push(HEADERS.map((h) => escapeCsvField(row[h])).join(','));
  }
  return lines.join('\n');
}
